import type { SxProps, Theme } from '@mui/material';

const NODE_GAP = 16;
/** From the bracket's line to the rows: room for the pill's right half and a gap after it. */
const INDENT = 36;
/** Half of the AND / OR pill: the line sits this far inside its container, so the pill is not cut off. */
const PILL_HALF = 24;

export const filterGroupsPanelSx: SxProps<Theme> = {
  minWidth: 0,
};

/** Overrides the shared 220px cap of the filter panel's content: it grows to half of the window, then scrolls. */
export const filterPanelContentSx = {
  '&&&': {
    maxHeight: '50vh !important',
    paddingTop: '0 !important',
  },
};

/** A row: its controls and the remove button in a line. */
export const filterLineSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

export const filterLineContentSx: SxProps<Theme> = {
  flexGrow: 1,
  minWidth: 0,
};

export const filterLineRemoveColumnSx: SxProps<Theme> = {
  flexShrink: 0,
};

/** The nodes of a group; the bracket joining them is drawn by the nodes, the AND / OR pill sits on it. */
export const filterTreeChildrenSx: SxProps<Theme> = {
  position: 'relative',
  pl: `${PILL_HALF + INDENT}px`,
};

/** The same without the bracket and the pill: a single node has nothing to join. */
export const filterSingleNodeChildrenSx: SxProps<Theme> = {
  ...filterTreeChildrenSx,
  '& > div::before': { display: 'none' },
};

/** The middle of a node's controls: its first row has the gap above it. */
const NODE_MIDDLE = `calc(50% + ${NODE_GAP / 2}px)`;
const BRACKET_RADIUS = 8;

/** What a node holds, which decides how far its arm of the bracket reaches. */
export type FilterTreeNodeKind = 'row' | 'group' | 'singleRowGroup';

/** The arm runs to the row; to the pill of a group; to the row of a group without a pill. */
const ARM_WIDTH_OF_KIND: Record<FilterTreeNodeKind, number> = {
  row: INDENT,
  group: INDENT + PILL_HALF,
  singleRowGroup: INDENT + PILL_HALF + INDENT,
};

/** A node and its part of the bracket: the arms start at the middle of the first node and end at the middle of the last. */
export function getFilterTreeNodeSx(kind: FilterTreeNodeKind): SxProps<Theme> {
  return {
    position: 'relative',
    // a group's gap above comes from its first row
    pt: kind === 'row' ? `${NODE_GAP}px` : 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -INDENT,
      top: 0,
      bottom: 0,
      width: ARM_WIDTH_OF_KIND[kind],
      borderStyle: 'solid',
      borderColor: 'divider',
      borderWidth: 0,
      borderLeftWidth: 1,
    },
    '&:first-of-type::before': {
      top: NODE_MIDDLE,
      borderTopWidth: 1,
      borderTopLeftRadius: BRACKET_RADIUS,
    },
    '&:last-of-type::before': {
      bottom: `calc(50% - ${NODE_GAP / 2}px)`,
      borderBottomWidth: 1,
      borderBottomLeftRadius: BRACKET_RADIUS,
    },
    // The first and the last node draw their arm with the bracket's corner; a node between them needs its own.
    '&:not(:first-of-type):not(:last-of-type)::after': {
      content: '""',
      position: 'absolute',
      left: -INDENT,
      top: NODE_MIDDLE,
      width: ARM_WIDTH_OF_KIND[kind],
      borderStyle: 'solid',
      borderColor: 'divider',
      borderWidth: 0,
      borderTopWidth: 1,
    },
    '&:only-of-type::before': {
      top: NODE_MIDDLE,
      bottom: 'auto',
      height: 0,
      borderLeftWidth: 0,
      borderRadius: 0,
    },
  };
}

/** The AND / OR pill sitting on the vertical line that joins the nodes of a group. */
export const logicOperatorPillSx: SxProps<Theme> = {
  position: 'absolute',
  left: PILL_HALF,
  top: `calc(50% + ${NODE_GAP / 2}px)`,
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
  minWidth: 0,
  height: 24,
  px: 1,
  borderRadius: 3,
  textTransform: 'none',
  fontWeight: 600,
  color: 'background.paper',
  backgroundColor: 'text.primary',
  '&:hover': { backgroundColor: 'text.secondary' },
};

export const filterFooterAddButtonsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

/** The footer's buttons keep the case of their texts instead of the theme's capitals. */
export const filterFooterSx = {
  '& .MuiButton-root': { textTransform: 'none' },
} satisfies SxProps<Theme>;
